import { memo, useCallback, useEffect, useMemo, useRef, useState, type PointerEvent as ReactPointerEvent } from "react";
import { Box } from "@mui/material";
import type { SxProps, Theme } from "@mui/material/styles";
import { OverlayRenderer, type OverlayAnnotation, type OverlayGeoJsonAnnotation } from "@impleotv-pm/st-overlay";
import {
  createDefaultRulerPoints,
  denormalizePlayerOverlayLayout,
  normalizePlayerOverlayLayout,
  type StoredPlayerOverlayLayout,
} from "../utils/playerOverlayLayout";
import type { AnnotationRenderItem } from "../types/annotation";

type VideoOverlayProps = {
  klvPacket: {
    time: Date;
    klv: Record<string, unknown>;
  } | null;
  layout: StoredPlayerOverlayLayout | null;
  onLayoutChange?: (layout: StoredPlayerOverlayLayout) => void;
  onMarkerStateChange?: (state: OverlayMarkerState) => void;
  editable?: boolean;
  focusObjectId?: string | null;
  gridEnabled?: boolean;
  rulerEnabled?: boolean;
  markerEnabled?: boolean;
  annotations?: AnnotationRenderItem[];
  sx?: SxProps<Theme>;
};

type GeoPoint = {
  latitude: number;
  longitude: number;
};

type OverlayPoint = {
  x: number;
  y: number;
};

type OverlayRulerState = {
  enabled: boolean;
  available: boolean;
  start: OverlayPoint | null;
  end: OverlayPoint | null;
  distanceMeters: number | null;
};

type OverlayMarkerState = {
  enabled: boolean;
  master: boolean;
  available: boolean;
  point: OverlayPoint | null;
  latitude: number | null;
  longitude: number | null;
};

const EMPTY_MARKER_STATE: OverlayMarkerState = {
  enabled: false,
  master: true,
  available: false,
  point: null,
  latitude: null,
  longitude: null,
};

const cloneOverlayGeoJson = (value: OverlayGeoJsonAnnotation): OverlayGeoJsonAnnotation =>
  JSON.parse(JSON.stringify(value)) as OverlayGeoJsonAnnotation;

const syncRendererAnnotations = (renderer: OverlayRenderer, annotations: AnnotationRenderItem[]) => {
  for (const annotation of renderer.getAnnotations()) {
    renderer.removeAnnotation(annotation.id);
  }

  const nextAnnotations: OverlayAnnotation[] = annotations.map((annotation) => ({
    id: annotation.id,
    name: annotation.name,
    geojson: cloneOverlayGeoJson(annotation.geojson as OverlayGeoJsonAnnotation),
    show: true,
    selected: false,
  }));

  if (nextAnnotations.length > 0) {
    renderer.addAnnotations(nextAnnotations);
  }
};

const getNumericField = (
  fields: Record<string, unknown> | null | undefined,
  ...keys: string[]
) => {
  if (!fields) {
    return null;
  }

  for (const key of keys) {
    const value = fields[key];
    if (typeof value === "number" && Number.isFinite(value)) {
      return value;
    }
    if (typeof value === "string") {
      const parsed = Number(value);
      if (Number.isFinite(parsed)) {
        return parsed;
      }
    }
  }

  return null;
};

const normalizeHeading = (value: number | null) => {
  if (value === null || !Number.isFinite(value)) {
    return null;
  }

  if (value >= 0 && value <= 360) {
    return value;
  }

  const normalized = ((value % 360) + 360) % 360;
  return normalized === 0 && value > 0 ? 360 : normalized;
};

const toRadians = (value: number) => (value * Math.PI) / 180;
const toDegrees = (value: number) => (value * 180) / Math.PI;
const clamp = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const midpoint = (first: GeoPoint, second: GeoPoint): GeoPoint => ({
  latitude: (first.latitude + second.latitude) / 2,
  longitude: (first.longitude + second.longitude) / 2,
});

const bearingBetween = (from: GeoPoint, to: GeoPoint) => {
  const latitude1 = toRadians(from.latitude);
  const latitude2 = toRadians(to.latitude);
  const deltaLongitude = toRadians(to.longitude - from.longitude);

  const y = Math.sin(deltaLongitude) * Math.cos(latitude2);
  const x =
    Math.cos(latitude1) * Math.sin(latitude2) -
    Math.sin(latitude1) * Math.cos(latitude2) * Math.cos(deltaLongitude);

  if (Math.abs(x) < Number.EPSILON && Math.abs(y) < Number.EPSILON) {
    return null;
  }

  return normalizeHeading(toDegrees(Math.atan2(y, x)));
};

const resolvePoint = (
  fields: Record<string, unknown> | null | undefined,
  latitudeKeys: string[],
  longitudeKeys: string[],
): GeoPoint | null => {
  const latitude = getNumericField(fields, ...latitudeKeys);
  const longitude = getNumericField(fields, ...longitudeKeys);
  if (latitude === null || longitude === null) {
    return null;
  }

  return { latitude, longitude };
};

const resolveFrameCenter = (
  packet: Record<string, unknown> | null | undefined,
) =>
  resolvePoint(
    packet,
    ["23", "FrameCenterLatitude", "Frame Center Latitude"],
    ["24", "FrameCenterLongitude", "Frame Center Longitude"],
  );

const resolveSensorPosition = (
  packet: Record<string, unknown> | null | undefined,
) =>
  resolvePoint(
    packet,
    ["13", "SensorLatitude", "Sensor Latitude"],
    ["14", "SensorLongitude", "Sensor Longitude"],
  );

const resolveOffsetCornerPoint = (
  packet: Record<string, unknown> | null | undefined,
  frameCenter: GeoPoint | null,
  latitudeKeys: string[],
  longitudeKeys: string[],
) => {
  if (!frameCenter) {
    return null;
  }

  const latitudeOffset = getNumericField(packet, ...latitudeKeys);
  const longitudeOffset = getNumericField(packet, ...longitudeKeys);
  if (latitudeOffset === null || longitudeOffset === null) {
    return null;
  }

  return {
    latitude: frameCenter.latitude + latitudeOffset,
    longitude: frameCenter.longitude + longitudeOffset,
  };
};

const resolveTerrainCorners = (
  packet: Record<string, unknown> | null | undefined,
) => {
  const frameCenter = resolveFrameCenter(packet);
  const fullCorners = [
    resolvePoint(
      packet,
      ["82", "CornerLatitudePoint1Full", "Corner Latitude Point 1 (Full)"],
      ["83", "CornerLongitudePoint1Full", "Corner Longitude Point 1 (Full)"],
    ),
    resolvePoint(
      packet,
      ["84", "CornerLatitudePoint2Full", "Corner Latitude Point 2 (Full)"],
      ["85", "CornerLongitudePoint2Full", "Corner Longitude Point 2 (Full)"],
    ),
    resolvePoint(
      packet,
      ["86", "CornerLatitudePoint3Full", "Corner Latitude Point 3 (Full)"],
      ["87", "CornerLongitudePoint3Full", "Corner Longitude Point 3 (Full)"],
    ),
    resolvePoint(
      packet,
      ["88", "CornerLatitudePoint4Full", "Corner Latitude Point 4 (Full)"],
      ["89", "CornerLongitudePoint4Full", "Corner Longitude Point 4 (Full)"],
    ),
  ];

  if (fullCorners.every(Boolean)) {
    return fullCorners as GeoPoint[];
  }

  const offsetCorners = [
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["26", "OffsetCornerLatitudePoint1", "Offset Corner Latitude Point 1"],
      ["27", "OffsetCornerLongitudePoint1", "Offset Corner Longitude Point 1"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["28", "OffsetCornerLatitudePoint2", "Offset Corner Latitude Point 2"],
      ["29", "OffsetCornerLongitudePoint2", "Offset Corner Longitude Point 2"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["30", "OffsetCornerLatitudePoint3", "Offset Corner Latitude Point 3"],
      ["31", "OffsetCornerLongitudePoint3", "Offset Corner Longitude Point 3"],
    ),
    resolveOffsetCornerPoint(
      packet,
      frameCenter,
      ["32", "OffsetCornerLatitudePoint4", "Offset Corner Latitude Point 4"],
      ["33", "OffsetCornerLongitudePoint4", "Offset Corner Longitude Point 4"],
    ),
  ];

  if (offsetCorners.every(Boolean)) {
    return offsetCorners as GeoPoint[];
  }

  return null;
};

const resolveTerrainOrientation = (
  packet: Record<string, unknown> | null | undefined,
) => {
  const corners = resolveTerrainCorners(packet);
  if (corners) {
    const topMidpoint = midpoint(corners[0], corners[1]);
    const bottomMidpoint = midpoint(corners[3], corners[2]);
    const cornerBearing = bearingBetween(bottomMidpoint, topMidpoint);
    if (cornerBearing !== null) {
      return cornerBearing;
    }
  }

  const sensorPosition = resolveSensorPosition(packet);
  const frameCenter = resolveFrameCenter(packet);
  if (sensorPosition && frameCenter) {
    return bearingBetween(sensorPosition, frameCenter);
  }

  return null;
};

const resolvePlatformHeading = (
  packet: Record<string, unknown> | null | undefined,
) =>
  normalizeHeading(
    getNumericField(
      packet,
      "5",
      "PlatformHeadingAngle",
      "Platform Heading Angle",
      "PlatformHeading",
    ),
  );

const VideoOverlay = memo(
  ({
    klvPacket,
    layout,
    onLayoutChange,
    onMarkerStateChange,
    editable = false,
    focusObjectId,
    gridEnabled = false,
    rulerEnabled = false,
    markerEnabled = false,
    annotations = [],
    sx,
  }: VideoOverlayProps) => {
    const rootRef = useRef<HTMLDivElement | null>(null);
    const canvasContainerRef = useRef<HTMLDivElement | null>(null);
    const rendererRef = useRef<OverlayRenderer | null>(null);
    const pendingPersistFrameRef = useRef<number | null>(null);
    const [size, setSize] = useState({ width: 0, height: 0 });
    const [rulerState, setRulerState] = useState<OverlayRulerState>({
      enabled: false,
      available: false,
      start: null,
      end: null,
      distanceMeters: null,
    });
    const [markerState, setMarkerState] = useState<OverlayMarkerState>(EMPTY_MARKER_STATE);
    const [activeRulerHandle, setActiveRulerHandle] = useState<"start" | "end" | null>(null);
    const [isMarkerDragging, setIsMarkerDragging] = useState(false);
    const packet = klvPacket?.klv ?? null;
    const heading = useMemo(() => resolvePlatformHeading(packet), [packet]);
    const terrainOrientation = useMemo(
      () => resolveTerrainOrientation(packet),
      [packet],
    );
    const latestLayoutRef = useRef<StoredPlayerOverlayLayout | null>(layout);
    const latestPacketStateRef = useRef({
      packet,
      heading,
      terrainOrientation,
    });
    const latestEditStateRef = useRef({
      editable,
      focusObjectId,
    });

    latestLayoutRef.current = layout;
    latestPacketStateRef.current = {
      packet,
      heading,
      terrainOrientation,
    };
    latestEditStateRef.current = {
      editable,
      focusObjectId,
    };

    const cancelPendingPersist = useCallback(() => {
      if (pendingPersistFrameRef.current !== null) {
        window.cancelAnimationFrame(pendingPersistFrameRef.current);
        pendingPersistFrameRef.current = null;
      }
    }, []);

    const syncRulerState = useCallback((renderer: OverlayRenderer | null = rendererRef.current) => {
      if (!renderer) {
        setRulerState({
          enabled: false,
          available: false,
          start: null,
          end: null,
          distanceMeters: null,
        });
        return;
      }

      setRulerState(renderer.getRulerState());
    }, []);

    const syncMarkerState = useCallback((renderer: OverlayRenderer | null = rendererRef.current) => {
      if (!renderer) {
        setMarkerState(EMPTY_MARKER_STATE);
        onMarkerStateChange?.(EMPTY_MARKER_STATE);
        return;
      }

      const nextState = renderer.getMarkerState();
      setMarkerState(nextState);
      onMarkerStateChange?.(nextState);
    }, [onMarkerStateChange]);

    const persistLayout = useCallback((renderer = rendererRef.current) => {
      if (!renderer || !onLayoutChange || size.width < 1 || size.height < 1) {
        return;
      }

      onLayoutChange(
        normalizePlayerOverlayLayout(
          renderer.serializeLayout(),
          size,
        ),
      );
    }, [onLayoutChange, size]);

    const schedulePersistLayout = useCallback((renderer = rendererRef.current) => {
      if (!renderer || !onLayoutChange || size.width < 1 || size.height < 1) {
        cancelPendingPersist();
        return;
      }

      cancelPendingPersist();
      pendingPersistFrameRef.current = window.requestAnimationFrame(() => {
        pendingPersistFrameRef.current = null;
        persistLayout(renderer);
      });
    }, [cancelPendingPersist, onLayoutChange, persistLayout, size.height, size.width]);

    useEffect(() => cancelPendingPersist, [cancelPendingPersist]);

    const ensureDefaultRulerState = useCallback((renderer = rendererRef.current) => {
      if (!renderer || size.width < 1 || size.height < 1) {
        return false;
      }

      const currentState = renderer.getRulerState();
      if (currentState.start && currentState.end) {
        return false;
      }

      renderer.loadRulerState(createDefaultRulerPoints(size));
      return true;
    }, [size]);

    const resolveClientPoint = useCallback((clientX: number, clientY: number) => {
      const element = rootRef.current;
      if (!element || size.width < 1 || size.height < 1) {
        return null;
      }

      const rect = element.getBoundingClientRect();
      return {
        x: clamp(clientX - rect.left, 0, size.width),
        y: clamp(clientY - rect.top, 0, size.height),
      };
    }, [size]);

    useEffect(() => {
      const element = rootRef.current;
      if (!element || typeof ResizeObserver === "undefined") {
        return;
      }

      const observer = new ResizeObserver((entries) => {
        const entry = entries[0];
        if (!entry) {
          return;
        }

        const nextWidth = Math.round(entry.contentRect.width);
        const nextHeight = Math.round(entry.contentRect.height);
        setSize((current) =>
          current.width === nextWidth && current.height === nextHeight
            ? current
            : { width: nextWidth, height: nextHeight },
        );
      });

      observer.observe(element);
      return () => observer.disconnect();
    }, []);

    useEffect(() => {
      const element = canvasContainerRef.current;
      if (!element || size.width < 1 || size.height < 1) {
        return;
      }

      const renderer = new OverlayRenderer({
        container: element,
        width: size.width,
        height: size.height,
      });

      const currentLayout = latestLayoutRef.current;
      if (currentLayout) {
        renderer.loadLayout({
          ...denormalizePlayerOverlayLayout(currentLayout, size),
          placeholder: null,
        });
      }
      syncRendererAnnotations(renderer, annotations);

      const currentPacketState = latestPacketStateRef.current;
      renderer.updatePacket(
        currentPacketState.packet
          ? {
              klv: currentPacketState.packet,
              vmti: null,
              heading: currentPacketState.heading,
              terrainOrientation: currentPacketState.terrainOrientation,
            }
          : {
              klv: null,
              vmti: null,
              heading: null,
              terrainOrientation: null,
            },
      );

      const currentEditState = latestEditStateRef.current;
      renderer.setEditMode(currentEditState.editable);
      const focusObjectId = currentEditState.focusObjectId;
      if (currentEditState.editable && focusObjectId) {
        window.requestAnimationFrame(() => {
          renderer.selectObject(focusObjectId);
        });
      }

      rendererRef.current = renderer;
      syncRulerState(renderer);
      syncMarkerState(renderer);

      return () => {
        cancelPendingPersist();
        persistLayout(renderer);
        renderer.destroy();
        rendererRef.current = null;
        syncRulerState(null);
        syncMarkerState(null);
      };
    }, [cancelPendingPersist, persistLayout, size.height, size.width, syncMarkerState, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || size.width < 1 || size.height < 1) {
        return;
      }

      const nextLayout = layout ? denormalizePlayerOverlayLayout(layout, size) : null;

      if (nextLayout) {
        renderer.loadLayout({
          ...nextLayout,
          placeholder: null,
        });
        syncRendererAnnotations(renderer, annotations);
      }
      syncRulerState(renderer);
      syncMarkerState(renderer);
    }, [annotations, layout, size.height, size.width, syncMarkerState, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer) {
        return;
      }

      renderer.updatePacket(
        packet
          ? {
              klv: packet,
              heading,
              terrainOrientation,
            }
          : {
              klv: null,
              vmti: null,
              heading: null,
              terrainOrientation: null,
            },
      );
      syncRulerState(renderer);
      syncMarkerState(renderer);
    }, [heading, packet, terrainOrientation, syncMarkerState, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer) {
        return;
      }

      syncRendererAnnotations(renderer, annotations);
    }, [annotations]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer) {
        return;
      }

      renderer.setEditMode(editable);
      if (editable && focusObjectId) {
        window.requestAnimationFrame(() => {
          renderer.selectObject(focusObjectId);
        });
      }
      syncRulerState(renderer);
      syncMarkerState(renderer);
    }, [editable, focusObjectId, syncMarkerState, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || renderer.isLayerEnabled("grid") === gridEnabled) {
        return;
      }

      renderer.setLayerEnabled("grid", gridEnabled);
      persistLayout(renderer);
    }, [gridEnabled, persistLayout]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || renderer.isRulerEnabled() === rulerEnabled) {
        return;
      }

      renderer.setRulerEnabled(rulerEnabled);
      syncRulerState(renderer);
      persistLayout(renderer);
    }, [persistLayout, rulerEnabled, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || renderer.isMarkerEnabled() === markerEnabled) {
        return;
      }

      renderer.setMarkerEnabled(markerEnabled);
      syncMarkerState(renderer);
      persistLayout(renderer);
    }, [markerEnabled, persistLayout, syncMarkerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer) {
        return;
      }

      syncMarkerState(renderer);
    }, [layout?.markerEnabled, layout?.markerMaster, layout?.markerPoint, layout?.markerCoordinate, syncMarkerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || !rulerEnabled) {
        return;
      }

      if (!ensureDefaultRulerState(renderer)) {
        return;
      }

      syncRulerState(renderer);
      persistLayout(renderer);
    }, [ensureDefaultRulerState, persistLayout, rulerEnabled, syncRulerState]);

    useEffect(() => {
      const renderer = rendererRef.current;
      if (!renderer || !onLayoutChange || size.width < 1 || size.height < 1) {
        return;
      }

      const unsubscribeTransforms = renderer.subscribeTransforms(() => {
        schedulePersistLayout(renderer);
      });

      return () => {
        cancelPendingPersist();
        persistLayout(renderer);
        unsubscribeTransforms();
      };
    }, [cancelPendingPersist, onLayoutChange, persistLayout, schedulePersistLayout, size.height, size.width]);

    useEffect(() => {
      if (!activeRulerHandle) {
        return;
      }

      const handlePointerMove = (event: PointerEvent) => {
        const renderer = rendererRef.current;
        const point = resolveClientPoint(event.clientX, event.clientY);
        if (!renderer || !point) {
          return;
        }

        const currentState = renderer.getRulerState();
        renderer.loadRulerState({
          start: activeRulerHandle === "start" ? point : currentState.start,
          end: activeRulerHandle === "end" ? point : currentState.end,
        });
        syncRulerState(renderer);
      };

      const handlePointerEnd = () => {
        persistLayout();
        setActiveRulerHandle(null);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerEnd);
      window.addEventListener("pointercancel", handlePointerEnd);

      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerEnd);
        window.removeEventListener("pointercancel", handlePointerEnd);
      };
    }, [activeRulerHandle, persistLayout, resolveClientPoint, syncRulerState]);

    useEffect(() => {
      if (!isMarkerDragging) {
        return;
      }

      const handlePointerMove = (event: PointerEvent) => {
        const renderer = rendererRef.current;
        const point = resolveClientPoint(event.clientX, event.clientY);
        if (!renderer || !point) {
          return;
        }

        if (!renderer.isMarkerMaster()) {
          renderer.setMarkerMaster(true);
        }
        renderer.loadMarkerState(point);
        syncMarkerState(renderer);
      };

      const handlePointerEnd = () => {
        persistLayout();
        syncMarkerState();
        setIsMarkerDragging(false);
      };

      window.addEventListener("pointermove", handlePointerMove);
      window.addEventListener("pointerup", handlePointerEnd);
      window.addEventListener("pointercancel", handlePointerEnd);

      return () => {
        window.removeEventListener("pointermove", handlePointerMove);
        window.removeEventListener("pointerup", handlePointerEnd);
        window.removeEventListener("pointercancel", handlePointerEnd);
      };
    }, [isMarkerDragging, persistLayout, resolveClientPoint, syncMarkerState]);

    const handleRulerPointerDown = useCallback(
      (role: "start" | "end") => (event: ReactPointerEvent<HTMLDivElement>) => {
        event.preventDefault();
        event.stopPropagation();
        setActiveRulerHandle(role);
      },
      [],
    );

    const handleMarkerPointerDown = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
      event.preventDefault();
      event.stopPropagation();
      setIsMarkerDragging(true);
    }, []);

    const showRulerHandles = !editable && rulerState.enabled && rulerState.available;
    const markerHandlePoint = !editable && markerState.enabled && markerState.available
      ? markerState.point
      : null;

    return (
      <Box
        ref={rootRef}
        sx={[
          {
            position: "relative",
            width: "100%",
            height: "100%",
            pointerEvents: "none",
            "& .video-overlay-canvas-root": {
              position: "absolute",
              inset: 0,
              width: "100%",
              height: "100%",
            },
            "& .video-overlay-ruler-handle": {
              position: "absolute",
              width: 24,
              height: 24,
              borderRadius: "50%",
              transform: "translate(-50%, -50%)",
              border: "2px solid rgba(7, 20, 23, 0.92)",
              boxShadow: "0 0 0 2px rgba(255, 255, 255, 0.22)",
              cursor: activeRulerHandle ? "grabbing" : "grab",
              pointerEvents: "auto",
              touchAction: "none",
              zIndex: 2,
            },
            "& .video-overlay-ruler-handle--start": {
              backgroundColor: "#f5d86f",
            },
            "& .video-overlay-ruler-handle--end": {
              backgroundColor: "#84d8ff",
            },
            "& .video-overlay-marker-handle": {
              position: "absolute",
              width: 36,
              height: 36,
              transform: "translate(-50%, -50%)",
              cursor: isMarkerDragging ? "grabbing" : "grab",
              pointerEvents: "auto",
              touchAction: "none",
              zIndex: 3,
            },
            "& canvas": {
              display: "block",
            },
          },
          ...(Array.isArray(sx) ? sx : sx ? [sx] : []),
        ]}
      >
        <Box
          ref={canvasContainerRef}
          className="video-overlay-canvas-root"
          sx={{ pointerEvents: editable ? "auto" : "none" }}
        />
        {showRulerHandles && rulerState.start ? (
          <Box
            className="video-overlay-ruler-handle video-overlay-ruler-handle--start"
            sx={{
              left: `${rulerState.start.x}px`,
              top: `${rulerState.start.y}px`,
            }}
            onPointerDown={handleRulerPointerDown("start")}
          />
        ) : null}
        {showRulerHandles && rulerState.end ? (
          <Box
            className="video-overlay-ruler-handle video-overlay-ruler-handle--end"
            sx={{
              left: `${rulerState.end.x}px`,
              top: `${rulerState.end.y}px`,
            }}
            onPointerDown={handleRulerPointerDown("end")}
          />
        ) : null}
        {markerHandlePoint ? (
          <Box
            className="video-overlay-marker-handle"
            sx={{
              left: `${markerHandlePoint.x}px`,
              top: `${markerHandlePoint.y}px`,
            }}
            onPointerDown={handleMarkerPointerDown}
          />
        ) : null}
      </Box>
    );
  },
);

VideoOverlay.displayName = "VideoOverlay";

export default VideoOverlay;
